import * as React from 'react';
import qs from 'qs';
import { PLATFORMS } from 'pages/views/devices/types';
import { getCFDAppUrl } from 'pages/views/devices/utils';
import { useQuery } from 'react-query';

export const ErrorMessages = {
  PLATFORM: 'Only Mac and Windows operating system are supported',
  NOTFOUND: 'No download link was found. We have been notified please try again later.',
  ERROR: 'There was an error with the download link. Try reloading the page.',
};

const FileExtensions = {
  [PLATFORMS.MAC]: 'dmg',
  [PLATFORMS.WINDOWS]: 'exe',
};

interface Asset {
  name: string;
  // eslint-disable-next-line camelcase
  browser_download_url: string;
}

interface HubRelease {
  assets: Asset[];
  // eslint-disable-next-line camelcase
  created_at: string;
  draft: boolean;
  name: string;
  prerelease: boolean;
  // eslint-disable-next-line camelcase
  published_at: string;
  // eslint-disable-next-line camelcase
  tag_name: string;
}

const HUB_RELEASES = 'hubReleases';

const getReleases = async (_key: string, version: string) => {
  const params = qs.stringify(
    {
      version,
    },
    { addQueryPrefix: true }
  );

  const response = await fetch(`${getCFDAppUrl('hub-backend')}/api/releases${params}`);
  const data = await response.json();
  return data;
};

const useGetAppDownloadUrl = () => {
  const [isAppDownloadAvailable, setIsAppDownloadAvailable] = React.useState(true);
  const [errorMessage, setErrorMessage] = React.useState('');
  const [appDownloadLinks, setAppDownloadLinks] = React.useState<
    { [key in PLATFORMS]: string } | Record<string, never>
  >({});

  const version = '>1.1.0-beta.1';

  const { data, error } = useQuery<HubRelease, [typeof HUB_RELEASES, string]>(
    [HUB_RELEASES, version],
    getReleases,
    {
      staleTime: 1000 * 60 * 60,
    }
  );

  const assets = data?.assets;
  const name = data?.name;

  React.useEffect(() => {
    if (error) {
      setIsAppDownloadAvailable(false);
      setErrorMessage(ErrorMessages.ERROR);
      if (window.newrelic) {
        window.newrelic.noticeError(error);
      }
    }

    if (!assets || !Array.isArray(assets)) {
      setIsAppDownloadAvailable(false);
      setErrorMessage(ErrorMessages.NOTFOUND);
    } else {
      setIsAppDownloadAvailable(true);
      const { browser_download_url: browserDownloadUrlMac } =
        assets.find(({ name }) => name.endsWith(FileExtensions[PLATFORMS.MAC])) || {};
      const { browser_download_url: browserDownloadUrlWindows } =
        assets.find(({ name }) => name.endsWith(FileExtensions[PLATFORMS.WINDOWS])) || {};

      if (browserDownloadUrlMac && browserDownloadUrlWindows) {
        setAppDownloadLinks({
          [PLATFORMS.MAC]: browserDownloadUrlMac,
          [PLATFORMS.WINDOWS]: browserDownloadUrlWindows,
        });
      } else {
        setIsAppDownloadAvailable(false);
        setErrorMessage(ErrorMessages.NOTFOUND);
      }
    }
  }, [assets, error]);

  return { appDownloadLinks, isAppDownloadAvailable, errorMessage, name };
};

export { useGetAppDownloadUrl };
